// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Header,
  Button,
  Icon,
  Form,
  Message,
  Segment,
  Divider,
  TextArea,
  Dropdown,
  Breadcrumb
} from 'semantic-ui-react';
import { brokerageApi } from '../api/brokerageApi';
import { brokeragePolicyTypeApi } from '../api/brokeragePolicyTypeApi';
import { policyTypeApi, PolicyType } from '../api/policyTypeApi';
import { providerApi, Provider } from '../api/providerApi';
import { templateApi, LOATemplate } from '../api/templateApi';
import { providerEmailOverridesApi, ProviderEmailOverride } from '../api/providerEmailOverridesApi';

function BrokerageProviderPolicyTypeEditPage() {
  const navigate = useNavigate();
  const { brokerageId, providerId, policyTypeId } = useParams<{
    brokerageId: string;
    providerId: string;
    policyTypeId: string;
  }>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [provider, setProvider] = useState<Provider | null>(null);
  const [policyType, setPolicyType] = useState<PolicyType | null>(null);
  const [templates, setTemplates] = useState<LOATemplate[]>([]);
  const [emailOverride, setEmailOverride] = useState<ProviderEmailOverride | null>(null);
  const [brokerageName, setBrokerageName] = useState<string>('');

  // Form state
  const [isEnabled, setIsEnabled] = useState(true);
  const [customTemplateId, setCustomTemplateId] = useState<string>('');
  const [overrideEmail, setOverrideEmail] = useState('');
  const [emailOverrideReason, setEmailOverrideReason] = useState('');
  const [emailSubjectTemplate, setEmailSubjectTemplate] = useState('');
  const [emailBodyTemplateHtml, setEmailBodyTemplateHtml] = useState('');
  const [emailBodyTemplateText, setEmailBodyTemplateText] = useState('');

  const loadData = useCallback(async () => {
    if (!brokerageId || !providerId || !policyTypeId) return;

    setLoading(true);
    setError(null);
    try {
      // Load brokerage name for breadcrumbs
      const brokerageData = await brokerageApi.getById(brokerageId);
      setBrokerageName(brokerageData.name);

      // Load provider details
      const providerData = await providerApi.getById(providerId);
      setProvider(providerData);

      // Load policy type details
      const policyTypeData = await policyTypeApi.getById(policyTypeId);
      setPolicyType(policyTypeData);

      // Load templates
      const templatesData = await templateApi.getAll();
      setTemplates(templatesData.content);

      // Load policy type configuration (may not exist yet)
      try {
        const configData = await brokeragePolicyTypeApi.getById(brokerageId, policyTypeId);
        setIsEnabled(configData.isEnabled || false);
        setCustomTemplateId(configData.customTemplateId || '');
        setEmailSubjectTemplate(configData.emailSubjectTemplate || '');
        setEmailBodyTemplateHtml(configData.emailBodyTemplateHtml || '');
        setEmailBodyTemplateText(configData.emailBodyTemplateText || '');
      } catch (err) {
        // Config doesn't exist yet - that's OK, we'll create it on save
        console.error('Error:', err);
      }

      // Load email override for this policy type (policy-type-level override)
      const emailOverridesData = await providerEmailOverridesApi.getPolicyTypeLevelOverrides(
        brokerageId
      );
      const override = emailOverridesData.find(
        (o) => o.providerId === providerId && o.policyTypeId === policyTypeId
      );
      if (override) {
        setEmailOverride(override);
        setOverrideEmail(override.overrideEmail);
        setEmailOverrideReason(override.emailOverrideReason || '');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [brokerageId, providerId, policyTypeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!brokerageId || !providerId || !policyTypeId) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Update or create policy type configuration
      const configPayload = {
        brokerageId,
        policyTypeId,
        isEnabled,
        customTemplateId: customTemplateId || undefined,
        emailSubjectTemplate: emailSubjectTemplate || undefined,
        emailBodyTemplateHtml: emailBodyTemplateHtml || undefined,
        emailBodyTemplateText: emailBodyTemplateText || undefined
      };

      await brokeragePolicyTypeApi.update(brokerageId, policyTypeId, configPayload);

      // Update or create email override if email is provided
      if (overrideEmail) {
        const overridePayload = {
          brokerageId,
          providerId,
          policyTypeId, // Policy-type-level override (HIGHEST PRIORITY)
          overrideEmail,
          emailOverrideReason
        };

        if (emailOverride) {
          await providerEmailOverridesApi.update(emailOverride.id!, overridePayload);
        } else {
          await providerEmailOverridesApi.create(overridePayload);
        }
      } else if (emailOverride) {
        // Email override was removed
        await providerEmailOverridesApi.delete(emailOverride.id!);
      }

      setSuccess(true);
      setTimeout(() => {
        navigate(`/brokerages/${brokerageId}/providers/${providerId}/policy-types`);
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const templateOptions = [
    { key: 'default', value: '', text: 'Use Default Template (from Provider or Brokerage)' },
    ...templates.map((t) => ({
      key: t.id,
      value: t.id,
      text: t.name
    }))
  ];

  if (loading) {
    return (
      <Container style={{ padding: '2rem' }}>
        <Message icon>
          <Icon name='circle notched' loading />
          <Message.Content>
            <Message.Header>Loading Configuration</Message.Header>
            Please wait while we load the policy type configuration...
          </Message.Content>
        </Message>
      </Container>
    );
  }

  return (
    <Container style={{ padding: '2rem' }}>
      <Breadcrumb>
        <Breadcrumb.Section link onClick={() => navigate('/brokerages')}>
          Brokerages
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section link onClick={() => navigate(`/brokerages/${brokerageId}/providers`)}>
          {brokerageName || 'Brokerage'}
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section link onClick={() => navigate(`/brokerages/${brokerageId}/providers/${providerId}/policy-types`)}>
          {provider?.name || 'Provider'}
        </Breadcrumb.Section>
        <Breadcrumb.Divider />
        <Breadcrumb.Section active>
          {policyType?.name || 'Policy Type'}
        </Breadcrumb.Section>
      </Breadcrumb>

      <Header as='h1' style={{ marginTop: '20px' }}>
        <Icon name='file alternate' />
        <Header.Content>
          Configure Policy Type: {policyType?.name || 'Unknown'}
          <Header.Subheader>
            Provider: {provider?.name || 'Unknown'} | Policy Type Override (Highest Priority)
          </Header.Subheader>
        </Header.Content>
      </Header>

      {error && (
        <Message negative onDismiss={() => setError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {success && (
        <Message positive>
          <Message.Header>Configuration Saved</Message.Header>
          <p>Policy type configuration updated successfully. Redirecting...</p>
        </Message>
      )}

      <Segment>
        <Header as='h3'>
          <Icon name='settings' />
          General Settings
        </Header>

        <Form>
          <Form.Checkbox
            label='Enable this policy type for the brokerage'
            checked={isEnabled}
            onChange={(_, data) => setIsEnabled(data.checked || false)}
          />
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='file' />
          LOA Template Override
        </Header>
        <Message warning>
          <Message.Header>
            <Icon name='warning sign' />
            Policy Type Override (Highest Priority)
          </Message.Header>
          <p>
            This configuration overrides provider-level and global defaults for this specific policy type.
            The template set here will be used instead of provider-level or brokerage-level templates.
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor='loa-template-dropdown'>LOA Template</label>
            <Dropdown
              id='loa-template-dropdown'
              placeholder='Select template'
              fluid
              selection
              options={templateOptions}
              value={customTemplateId}
              onChange={(_, data) => setCustomTemplateId(data.value as string)}
            />
          </Form.Field>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='mail' />
          Email Override
        </Header>
        <Message error>
          <Message.Header>
            <Icon name='warning sign' />
            Policy Type Override (Highest Priority)
          </Message.Header>
          <p>
            This email address will be used for THIS SPECIFIC policy type only. It overrides both
            provider-level and brokerage-level email addresses.
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor='override-email-input'>Override Email Address</label>
            <Form.Input
              id='override-email-input'
              type='email'
              placeholder='policytype-specific@example.com'
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
            />
            <small>Leave empty to use provider-level or provider default email</small>
          </Form.Field>

          <Form.Field>
            <label htmlFor='override-reason-textarea'>Reason for Override (Optional)</label>
            <Form.TextArea
              id='override-reason-textarea'
              placeholder='e.g., Dedicated representative for this policy type'
              value={emailOverrideReason}
              onChange={(e) => setEmailOverrideReason(e.target.value)}
              rows={2}
            />
          </Form.Field>
        </Form>
      </Segment>

      <Segment>
        <Header as='h3'>
          <Icon name='envelope' />
          Email Notification Templates
        </Header>
        <Message error>
          <Message.Header>
            <Icon name='warning sign' />
            Policy Type Override (Highest Priority)
          </Message.Header>
          <p>
            These templates will override provider-level and brokerage-level templates for THIS
            specific policy type. Leave empty to use provider or brokerage defaults. Use{' '}
            <code>{'{{variable}}'}</code> for subject and <code>{'[[${variable}]]'}</code> for HTML
            body.
          </p>
        </Message>

        <Form>
          <Form.Field>
            <label htmlFor='email-subject-template-input'>Email Subject Template</label>
            <Form.Input
              id='email-subject-template-input'
              placeholder='LOA Update: {{policyType}} from {{providerName}}'
              value={emailSubjectTemplate}
              onChange={(e) => setEmailSubjectTemplate(e.target.value)}
            />
            <small>
              Available variables: {'{{'}advisorName{'}}'}
              , {'{{'}clientName{'}}'}
              , {'{{'}policyType{'}}'}
              , {'{{'}providerName{'}}'}
            </small>
          </Form.Field>

          <Form.Field>
            <label htmlFor='email-body-html-textarea'>Email Body Template (HTML)</label>
            {/*
              The placeholder below shows the admin an example of Thymeleaf
              interpolation — `[[${var}]]` is Thymeleaf, NOT a JS template
              literal. It is written as a real template literal with escaped
              `\${` so the intent is explicit; as a plain quoted string it reads
              to static analysis as a mis-quoted template. Output is identical.
            */}
            <TextArea
              id='email-body-html-textarea'
              placeholder={`<h1>Dear [[\${advisorName}]],</h1><p>LOA update for [[\${policyType}]]</p>`}
              value={emailBodyTemplateHtml}
              onChange={(e) => setEmailBodyTemplateHtml(e.target.value)}
              rows={8}
              style={{ fontFamily: 'monospace' }}
            />
            <small>
              Thymeleaf syntax: <code>[[${'{'}variable{'}'}]]</code> or{' '}
              <code>th:text="${'{'}variable{'}'}"</code>
            </small>
          </Form.Field>

          <Form.Field>
            <label htmlFor='email-body-text-textarea'>Email Body Template (Plain Text - Fallback)</label>
            <TextArea
              id='email-body-text-textarea'
              placeholder='Dear {{advisorName}}, LOA update for {{policyType}}'
              value={emailBodyTemplateText}
              onChange={(e) => setEmailBodyTemplateText(e.target.value)}
              rows={6}
              style={{ fontFamily: 'monospace' }}
            />
            <small>
              Plain text fallback for non-HTML email clients. Use {'{{'}variable{'}}'}
            </small>
          </Form.Field>
        </Form>
      </Segment>

      <Divider />

      <Button.Group>
        <Button
          onClick={() =>
            navigate(`/brokerages/${brokerageId}/providers/${providerId}/policy-types`)
          }
        >
          Cancel
        </Button>
        <Button.Or />
        <Button primary onClick={handleSave} loading={saving} disabled={saving}>
          <Icon name='save' />
          Save Configuration
        </Button>
      </Button.Group>
    </Container>
  );
}

export default BrokerageProviderPolicyTypeEditPage;
